import { queryOptions } from "@tanstack/react-query";
import {
  fetchPrompts,
  fetchPrompt,
  fetchPromptVersions,
  fetchUsedInValues,
  fetchVariablesByUsedIn,
  fetchPromptLlmModels,
  fetchPromptLabels,
  fetchPromptUsage,
  fetchVariableUsages,
  previewBulkReplace,
  type PromptsListFilters,
} from "@/features/prompts/api/prompts.api";

export interface PromptBulkPreviewInput {
  find: string;
  replace: string;
  matchCase: boolean;
  wholeWord: boolean;
  regex: boolean;
}

// ─── Key factory ─────────────────────────────────────────────────────

export const promptsKeys = {
  all: ["prompts"] as const,
  list: (filters?: PromptsListFilters) => ["prompts", "list", filters ?? {}] as const,
  detail: (id: string) => ["prompts", "detail", id] as const,
  versions: (id: string) => ["prompts", "versions", id] as const,
  usedInValues: ["prompts", "usedInValues"] as const,
  variables: (usedIn: string) => ["prompts", "variables", usedIn] as const,
  llmModels: ["prompts", "llmModels"] as const,
  labels: ["prompts", "labels"] as const,
  variableUsages: (name: string) => ["prompts", "variableUsages", name] as const,
  bulkPreview: (input: PromptBulkPreviewInput) => ["prompts", "bulk-preview", input] as const,
  usage: (windowHours: number) => ["prompts", "usage", windowHours] as const,
};

const PROMPT_USAGE_WINDOW_HOURS = 168;

// ─── Query options ───────────────────────────────────────────────────

export function promptsListQueryOptions(filters?: PromptsListFilters) {
  return queryOptions({
    queryKey: promptsKeys.list(filters),
    queryFn: () => fetchPrompts(filters),
    placeholderData: (previous) => previous,
  });
}

export function promptDetailQueryOptions(id: string) {
  return queryOptions({
    queryKey: promptsKeys.detail(id),
    queryFn: () => fetchPrompt(id),
    enabled: !!id,
  });
}

export function promptVersionsQueryOptions(id: string) {
  return queryOptions({
    queryKey: promptsKeys.versions(id),
    queryFn: () => fetchPromptVersions(id),
    enabled: !!id,
  });
}

export function promptUsedInValuesQueryOptions() {
  return queryOptions({
    queryKey: promptsKeys.usedInValues,
    queryFn: () => fetchUsedInValues(),
    staleTime: 30_000,
  });
}

export function promptVariablesQueryOptions(usedIn: string) {
  return queryOptions({
    queryKey: promptsKeys.variables(usedIn),
    queryFn: () => fetchVariablesByUsedIn(usedIn),
    enabled: !!usedIn,
    staleTime: 30_000,
  });
}

export function promptLabelsQueryOptions() {
  return queryOptions({
    queryKey: promptsKeys.labels,
    queryFn: () => fetchPromptLabels(),
    staleTime: 30_000,
  });
}

export function promptBulkPreviewQueryOptions(input: PromptBulkPreviewInput) {
  return queryOptions({
    queryKey: promptsKeys.bulkPreview(input),
    queryFn: () => previewBulkReplace(input),
    enabled: input.find.trim().length > 0,
  });
}

export function promptVariableUsagesQueryOptions(name: string) {
  return queryOptions({
    queryKey: promptsKeys.variableUsages(name),
    queryFn: () => fetchVariableUsages(name),
    enabled: name.length > 0,
  });
}

export function promptsLlmModelsQueryOptions() {
  return queryOptions({
    queryKey: promptsKeys.llmModels,
    queryFn: () => fetchPromptLlmModels(),
    staleTime: 60_000,
  });
}

export function promptsUsageQueryOptions(windowHours = PROMPT_USAGE_WINDOW_HOURS) {
  return queryOptions({
    queryKey: promptsKeys.usage(windowHours),
    queryFn: () => fetchPromptUsage(windowHours),
    staleTime: 60_000,
  });
}

export { PROMPT_USAGE_WINDOW_HOURS };
